/**
 * reviewSummaryUtils — Shared utilities for converting raw review-agent
 * check data into human-friendly labels used on DAG nodes, the inspector
 * quality strip, and the activity log.
 */

// ── Types ────────────────────────────────────────────────────────────

export interface CheckResult {
  name: string;
  passed: boolean;
  measured?: number;
  threshold?: number;
  details?: Record<string, unknown>;
}

export interface Correction {
  check_name: string;
  fix_type: string;
  description: string;
  succeeded: boolean;
}

export interface ReviewWarning {
  check_name: string;
  severity: string;
  message: string;
}

export type CheckStatus = 'pass' | 'fixed' | 'warn' | 'fail';

export interface CategorizedCheck {
  name: string;
  status: CheckStatus;
  displayLabel: string;
}

// ── Display config ───────────────────────────────────────────────────

interface CheckDisplayEntry {
  passLabel: string;
  failLabel: string;
}

const CHECK_DISPLAY_CONFIG: Record<string, CheckDisplayEntry> = {
  row_count_delta:           { passLabel: 'No data loss',           failLabel: 'Row count changed' },
  join_fanout:               { passLabel: 'No fanout',              failLabel: 'Join fanout detected' },
  null_introduction:         { passLabel: 'No null introduction',   failLabel: 'Nulls introduced' },
  low_match_rate:            { passLabel: 'Good match rate',        failLabel: 'Low match rate' },
  match_rate:                { passLabel: 'Good match rate',        failLabel: 'Low match rate' },
  high_null_extraction:      { passLabel: 'Extraction OK',          failLabel: 'High null extraction' },
  extraction_completeness:   { passLabel: 'Extraction OK',          failLabel: 'Low extraction rate' },
  high_null_classification:  { passLabel: 'Classification OK',      failLabel: 'High null classification' },
  classification_skew:       { passLabel: 'Classification balanced', failLabel: 'Classification skewed' },
  classification_distribution: { passLabel: 'Distribution OK',      failLabel: 'Distribution issue' },
  off_category_values:       { passLabel: 'Categories valid',       failLabel: 'Off-category values' },
  low_enrichment_rate:       { passLabel: 'Enrichment OK',          failLabel: 'Low enrichment rate' },
  enrichment_rate:           { passLabel: 'Enrichment OK',          failLabel: 'Low enrichment rate' },
};

// ── Helpers ──────────────────────────────────────────────────────────

function labelForCheck(check: CheckResult, corrected: boolean): string {
  const config = CHECK_DISPLAY_CONFIG[check.name];
  if (!config) return check.name;

  if (check.passed || corrected) {
    return corrected ? `${config.failLabel} (fixed)` : config.passLabel;
  }
  return config.failLabel;
}

// ── Public API ───────────────────────────────────────────────────────

/**
 * Compact one-liner for DAG nodes and activity log.
 * Returns `null` when there are no checks.
 *
 * Examples:
 *   "No data loss · No fanout · No nulls"
 *   "Join fanout detected (fixed) · No nulls"
 */
export function formatCheckSummaryLine(
  checks: CheckResult[] | undefined,
  corrections: Correction[] | undefined,
): string | null {
  if (!checks || checks.length === 0) return null;

  const correctedNames = new Set(
    (corrections ?? []).filter((c) => c.succeeded).map((c) => c.check_name),
  );

  return checks
    .map((ch) => labelForCheck(ch, correctedNames.has(ch.name)))
    .join(' · ');
}

/**
 * Structured array for the inspector quality chip strip.
 * Each check gets a status + human label.
 */
export function categorizeChecks(
  checks: CheckResult[] | undefined,
  corrections: Correction[] | undefined,
  warnings: ReviewWarning[] | undefined,
): CategorizedCheck[] {
  if (!checks || checks.length === 0) return [];

  const correctedNames = new Set(
    (corrections ?? []).filter((c) => c.succeeded).map((c) => c.check_name),
  );
  const warnedNames = new Set((warnings ?? []).map((w) => w.check_name));

  return checks.map((ch) => {
    const corrected = correctedNames.has(ch.name);
    let status: CheckStatus;
    if (corrected) {
      status = 'fixed';
    } else if (ch.passed) {
      status = 'pass';
    } else if (warnedNames.has(ch.name)) {
      status = 'warn';
    } else {
      status = 'fail';
    }

    return {
      name: ch.name,
      status,
      displayLabel: labelForCheck(ch, corrected),
    };
  });
}

/**
 * Overall color for a set of categorized checks.
 * Returns 'green' | 'blue' | 'amber' based on worst status.
 */
export function overallCheckColor(
  categorized: CategorizedCheck[],
): 'green' | 'blue' | 'amber' {
  const hasWarnOrFail = categorized.some(
    (c) => c.status === 'warn' || c.status === 'fail',
  );
  if (hasWarnOrFail) return 'amber';

  const hasFixed = categorized.some((c) => c.status === 'fixed');
  if (hasFixed) return 'blue';

  return 'green';
}
